import React, { Fragment, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Grid, Button, Tab, Typography } from '@material-ui/core';
import { TabPanel, TabList, TabContext } from '@material-ui/lab';

import { useTranslation } from 'react-i18next';
import { LogIn, LogOut } from 'react-feather';
import EditIcon from '@material-ui/icons/Edit';

import {
  JwlTable,
  ErrorComponent,
  LoadingSpinner,
  SectionCard,
  Chip,
  DetailsList
} from '../../../../components';

import { StyledDivider } from './styles';
import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';

import { userLanguage } from '../../../../core/AuthContext';
import { PlatformNavigation } from '../../navigation/PlatformNavigation';
import { Exception } from '@jewelutions/core-common-domain';
import { GQLPieceReferenceHelper } from '../../../../operations/helpers/GQLPieceHelper';
import { GQLAccessoryReferenceHelper } from '../../../../operations/helpers/GQLAccessoryHelper';
import PriceLabel from '../../../../components/PriceLabel';
import { CommonLogic } from '@jewelutions/core-common-logic';
import { OperationsHelper } from '../../../../operations/OperationHelper';
import { saveCsvFile } from '../../../../core/utils';
import { UploadButton } from '../../../../components/UploadButtonComponent';
import { successNotification } from '../../../../core/utils';
import { toast } from 'react-toastify';
import PlatformSaleCatalogDuplicateDialog from '../PlatformSaleCatalogDuplicateDialog';
import PlatformSaleCatalogEditDialog from '../PlatformSaleCatalogEditDialog';

const PlatformSaleCatalogDetails: React.FC = () => {
  const [errorComponentState, setErrorComponentState] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const { t: translate } = useTranslation();
  const platformSaleCatalogId = PlatformNavigation.platformSaleCatalogDetails.paramsFromPath(useRouteMatch())
    .platformSaleCatalogId;
  const [tabValue, setTabValue] = useState('EUR');
  const history = useHistory();

  const handleTabChange = (event: any, newValue: string) => {
    setTabValue(newValue);
  };

  const [
    publishPlatformSaleCatalog,
    {}
  ] = GQLOperationBuilder.platformSaleCatalog.publishPlatformSaleCatalog();

  const [deletePlatformSaleCatalog, {}] = GQLOperationBuilder.platformSaleCatalog.deletePlatformSaleCatalog();

  const [
    exportPlatformSaleCatalogAccessoryPricingCsvFromCatalog,
    {}
  ] = GQLOperationBuilder.platformSaleCatalog.exportPlatformAccessoryPricingCsvFromCatalogLazy({
    onCompleted: result => {
      const bytes = CommonLogic.Base64.fromBase64(OperationsHelper.retrieveBytesFromGQLResponse(result));
      saveCsvFile(bytes, 'platform_sale_catalog_accessory_pricing.csv');
      successNotification(
        toast,
        'Delivery unit sale catalog accessories pricing downloaded successfully',
        () => {}
      );
    }
  });
  const [
    exportPlatformSaleCatalogPiecePricingCsvFromCatalog,
    {}
  ] = GQLOperationBuilder.platformSaleCatalog.exportPlatformPiecePricingCsvFromCatalogLazy({
    onCompleted: result => {
      const bytes = CommonLogic.Base64.fromBase64(OperationsHelper.retrieveBytesFromGQLResponse(result));
      saveCsvFile(bytes, 'platform_sale_catalog_piece_pricing.csv');
      successNotification(
        toast,
        'Platform unit sale catalog pieces pricing downloaded successfully',
        () => {}
      );
    }
  });

  const [
    importPlatformSaleCatalogPiecePricingCsvIntoCatalog,
    {}
  ] = GQLOperationBuilder.platformSaleCatalog.importPlatformPiecePricingCsvIntoCatalog({
    onCompleted: async () => {
      successNotification(toast, 'Platform sale catalog pieces pricing imported successfully', () => {});
      await reloadData();
    }
  });

  const [
    importPlatformSaleCatalogAccessoryPricingCsvIntoCatalog,
    {}
  ] = GQLOperationBuilder.platformSaleCatalog.importPlatformAccessoryPricingCsvIntoCatalog({
    onCompleted: async () => {
      successNotification(toast, 'Platform sale catalog accessories pricing imported successfully', () => {});
      await reloadData();
    }
  });

  const {
    data: saleCatalogsData,
    loading: saleCatalogsLoading,
    error: saleCatalogsError,
    refetch: refetchSaleCatalogs
  } = GQLOperationBuilder.platformSaleCatalog.getPlatformSaleCatalogs({ includeOnlyActiveContracts: true });

  const {
    data: merchantContractsData,
    loading: merchantContractsLoading,
    error: merchantContractsError,
    refetch: merchantContractsRefetch
  } = GQLOperationBuilder.platformSaleCatalog.getPlatformSaleCatalogWithPlatformMerchantContracts({
    includeOnlyActiveContracts: true
  });

  const reloadData = async () => {
    refetchSaleCatalogs();
    merchantContractsRefetch();
  };

  if (saleCatalogsLoading || merchantContractsLoading) return <LoadingSpinner />;

  if (saleCatalogsError && !errorComponentState)
    return (
      <ErrorComponent
        close={setErrorComponentState}
        details={saleCatalogsError?.stack}
        callback={refetchSaleCatalogs}
        message={saleCatalogsError.message}
      />
    );

  const currentplatformSaleCatalog = GQLOperationBuilder.platformSaleCatalog
    .platformSaleCatalogArrayFromResponse(saleCatalogsData)
    .find(catalog => catalog.id === platformSaleCatalogId);
  if (currentplatformSaleCatalog === undefined || currentplatformSaleCatalog === null)
    throw new Exception(`Cannot load platform sale catalog ${platformSaleCatalogId}`);

  const associatedActiveMerchantContract = GQLOperationBuilder.platformSaleCatalog
    .platformSaleCatalogWithPlatformMerchantContractArrayFromResponse(merchantContractsData)
    .filter(m => m.platformSaleCatalog.id === currentplatformSaleCatalog.id);

  const getPieceInventoryRows = () => {
    return currentplatformSaleCatalog.deliveryUnitSaleCatalog.inventoryCatalog.pieceInventorys.map(
      pieceInventory => {
        return {
          id: pieceInventory.id,
          cells: [
            pieceInventory.pieceTitle.en,
            pieceInventory.pieceReference.pieceConfiguration.sizeType,
            pieceInventory.pieceReference.pieceConfiguration.materialType,
            pieceInventory.pieceReference.pieceConfiguration.platingType,
            pieceInventory.inventoryReference
          ]
        };
      }
    );
  };

  const getAccessoriesInventoryRows = () => {
    return currentplatformSaleCatalog.deliveryUnitSaleCatalog.inventoryCatalog.accessoryInventorys.map(
      accessoryInventory => {
        return {
          id: accessoryInventory.id,
          cells: [
            accessoryInventory.accessoryTitle.en,
            accessoryInventory.accessoryReference.accessoryConfiguration.sizeType,
            accessoryInventory.accessoryReference.accessoryConfiguration.materialType,
            accessoryInventory.accessoryReference.accessoryConfiguration.platingType,
            accessoryInventory.inventoryReference
          ]
        };
      }
    );
  };

  const getMerchantContractsRows = () => {
    return associatedActiveMerchantContract.map(merchantContract => {
      return {
        id: merchantContract?.platformMerchantContract?.id,
        cells: [
          merchantContract?.platformMerchantContract?.producerParty?.name,
          merchantContract?.platformMerchantContract?.producerDeliveryUnit?.name,
          merchantContract?.platformMerchantContract?.merchantParty?.name,
          new Date(Date.parse(merchantContract?.platformMerchantContract?.updatedAt)).toLocaleString()
        ]
      };
    });
  };

  const navigateToMerchantContractsDetails = (merchantContractId: string) => {
    history.push(
      PlatformNavigation.platformMerchantContractDetails.pathFromParams({
        merchantContractId: merchantContractId
      })
    );
  };

  return (
    <Fragment>
      <Grid item style={{ marginTop: '8px' }}>
        <div style={{ display: 'flex' }}>
          <Typography style={{ marginRight: '2px' }} variant="h4">
            {currentplatformSaleCatalog.name.en}
          </Typography>
          {!currentplatformSaleCatalog.publishedAt && (
            <EditIcon
              fontSize="small"
              style={{ cursor: 'pointer' }}
              onClick={() => setShowEditDialog(true)}
            />
          )}
        </div>
        <SectionCard title="Overview" fitContent>
          <DetailsList
            list={[
              {
                primary: 'Delivery Unit Sale Catalog name',
                secondary: currentplatformSaleCatalog.deliveryUnitSaleCatalog.name[userLanguage]
              },
              {
                primary: 'Delivery Unit Sale Catalog description',
                secondary: currentplatformSaleCatalog.deliveryUnitSaleCatalog.description[userLanguage]
              },

              {
                primary: 'Platform Sale Catalog Name',
                secondary: currentplatformSaleCatalog.name[userLanguage]!
              },
              {
                primary: 'Platform Sale Catalog Description',
                secondary: currentplatformSaleCatalog.description[userLanguage]!
              },
              {
                primary: 'Published',
                secondary: (
                  <Chip
                    size="small"
                    mr={1}
                    mb={1}
                    label={currentplatformSaleCatalog.isPublished ? 'YES' : 'NO'}
                    $active={!!currentplatformSaleCatalog.isPublished}
                  />
                )
              },
              {
                primary: 'Active Merchant contracts',
                secondary: (
                  <Chip
                    size="small"
                    mr={1}
                    mb={1}
                    label={associatedActiveMerchantContract.length}
                    $active={associatedActiveMerchantContract.length > 0}
                  />
                )
              },
              {
                primary: 'Updated At',
                secondary: new Date(Date.parse(currentplatformSaleCatalog.updatedAt)).toLocaleString()
              }
            ]}
          />
          {!currentplatformSaleCatalog.isPublished && (
            <Button
              variant={'contained'}
              color={'primary'}
              onClick={() =>
                publishPlatformSaleCatalog({
                  variables: { platformSaleCatalogId: currentplatformSaleCatalog.id }
                }).then(_res => {
                  refetchSaleCatalogs();
                })
              }
              disabled={currentplatformSaleCatalog.isPublished!}
            >
              Publish sale catalog
            </Button>
          )}
          <Button
            variant={'contained'}
            color={'primary'}
            onClick={() => {
              setShowDuplicateDialog(true);
            }}
            style={{ marginLeft: '4px' }}
          >
            Duplicate
          </Button>
          {!currentplatformSaleCatalog.isPublished && (
            <Button
              variant={'contained'}
              color={'primary'}
              onClick={() =>
                deletePlatformSaleCatalog({
                  variables: {
                    platformSaleCatalogId: currentplatformSaleCatalog.id
                  }
                }).then(_result => {
                  successNotification(toast, 'Platform sale catalog deleted successfully', () => {});
                  history.push(PlatformNavigation.platformSaleCatalogs.pathFromParams({}));
                })
              }
              disabled={currentplatformSaleCatalog.isPublished!}
              style={{ marginLeft: '4px' }}
            >
              Delete
            </Button>
          )}
        </SectionCard>
      </Grid>

      {associatedActiveMerchantContract.length > 0 && (
        <Grid item style={{ marginTop: '8px' }}>
          <SectionCard title="">
            <JwlTable
              title="Active merchant contracts associated with current platform sale catalog"
              description="The platform contracts with specific merchants. Each merchant contract defines the platform commitments to the merchant in regards with pieces / accessories inventory & platform prices."
              columns={['Producer Party Name', 'Producer Delivery Unit', 'Merchant Party Name', 'Updated At']}
              rows={getMerchantContractsRows()}
              onTableRowClick={navigateToMerchantContractsDetails}
            />
          </SectionCard>
        </Grid>
      )}

      {currentplatformSaleCatalog.deliveryUnitSaleCatalog.pricingCatalogs.length > 0 && (
        <Grid item style={{ marginTop: '8px' }}>
          <SectionCard title="Selling prices">
            <TabContext value={tabValue}>
              <TabList
                onChange={(event, tabIndex) => handleTabChange(event, tabIndex)}
                aria-label="currency-tabs"
              >
                {currentplatformSaleCatalog.deliveryUnitSaleCatalog.pricingCatalogs.map(
                  deliveryUnitPricingCatalog => {
                    return (
                      <Tab
                        label={`${deliveryUnitPricingCatalog.currency} Pricing Catalog`}
                        value={deliveryUnitPricingCatalog.currency}
                      />
                    );
                  }
                )}
              </TabList>
              {currentplatformSaleCatalog.deliveryUnitSaleCatalog.pricingCatalogs.map(
                deliveryUnitPricingCatalog => {
                  const platformSellingPricingCatalog = currentplatformSaleCatalog.platformSellingPricingCatalogs.find(
                    c => c.currency === deliveryUnitPricingCatalog.currency
                  );
                  if (platformSellingPricingCatalog === undefined || platformSellingPricingCatalog === null)
                    throw new Exception(
                      `Cannot locate platform selling pricing catalog for currency ${deliveryUnitPricingCatalog.currency}`
                    );

                  const merchantReferenceRetailPricingCatalog = currentplatformSaleCatalog.merchantReferenceRetailPricingCatalogs.find(
                    c => c.currency === deliveryUnitPricingCatalog.currency
                  );
                  if (
                    merchantReferenceRetailPricingCatalog === undefined ||
                    merchantReferenceRetailPricingCatalog === null
                  )
                    throw new Exception(
                      `Cannot locate merchant retail reference pricing catalog for currency ${deliveryUnitPricingCatalog.currency}`
                    );

                  const getPiecePricingRows = () => {
                    return deliveryUnitPricingCatalog.pieces.map(deliveryUnitPiecePrice => {
                      const platformSellingPrice = platformSellingPricingCatalog.pieces.find(p =>
                        GQLPieceReferenceHelper.identical(
                          deliveryUnitPiecePrice.pieceReference,
                          p.pieceReference
                        )
                      );
                      if (platformSellingPrice === undefined)
                        throw new Exception(
                          `Cannot match platform selling price for piece reference ${JSON.stringify(
                            deliveryUnitPiecePrice.pieceReference
                          )}`
                        );

                      const merchantReferenceRetailPrice = merchantReferenceRetailPricingCatalog.pieces.find(
                        p =>
                          GQLPieceReferenceHelper.identical(
                            deliveryUnitPiecePrice.pieceReference,
                            p.pieceReference
                          )
                      );
                      if (merchantReferenceRetailPrice === undefined)
                        throw new Exception(
                          `Cannot match merchant reference retail price for piece reference ${JSON.stringify(
                            deliveryUnitPiecePrice.pieceReference
                          )}`
                        );

                      return {
                        id: deliveryUnitPiecePrice.pieceReference.pieceId,
                        cells: [
                          deliveryUnitPiecePrice.pieceReference.pieceConfiguration.sizeType,
                          deliveryUnitPiecePrice.pieceReference.pieceConfiguration.materialType,
                          deliveryUnitPiecePrice.pieceReference.pieceConfiguration.platingType,
                          deliveryUnitPiecePrice.pieceReference.pieceConfiguration.stonesPlacementType,
                          <PriceLabel price={deliveryUnitPiecePrice.price} />,
                          <PriceLabel price={platformSellingPrice.price} />,
                          <PriceLabel price={merchantReferenceRetailPrice.price} />
                        ]
                      };
                    });
                  };

                  const getAccessoryPricingRows = () => {
                    return deliveryUnitPricingCatalog.accessories.map(deliveryUnitAccessoryPrice => {
                      const platformSellingPrice = platformSellingPricingCatalog.accessories.find(a =>
                        GQLAccessoryReferenceHelper.identical(
                          deliveryUnitAccessoryPrice.accessoryReference,
                          a.accessoryReference
                        )
                      );
                      if (platformSellingPrice === undefined)
                        throw new Exception(
                          `Cannot match platform selling price for accessory reference ${JSON.stringify(
                            deliveryUnitAccessoryPrice.accessoryReference
                          )}`
                        );

                      const merchantReferenceRetailPrice = merchantReferenceRetailPricingCatalog.accessories.find(
                        a =>
                          GQLAccessoryReferenceHelper.identical(
                            deliveryUnitAccessoryPrice.accessoryReference,
                            a.accessoryReference
                          )
                      );
                      if (merchantReferenceRetailPrice === undefined)
                        throw new Exception(
                          `Cannot match merchant reference retail price for accessory reference ${JSON.stringify(
                            deliveryUnitAccessoryPrice.accessoryReference
                          )}`
                        );

                      return {
                        id: deliveryUnitAccessoryPrice.accessoryReference.accessoryId,
                        cells: [
                          deliveryUnitAccessoryPrice.accessoryReference.accessoryConfiguration.sizeType,
                          deliveryUnitAccessoryPrice.accessoryReference.accessoryConfiguration.materialType,
                          deliveryUnitAccessoryPrice.accessoryReference.accessoryConfiguration.platingType,
                          <PriceLabel price={deliveryUnitAccessoryPrice.price} />,
                          <PriceLabel price={platformSellingPrice.price} />,
                          <PriceLabel price={merchantReferenceRetailPrice.price} />
                        ]
                      };
                    });
                  };

                  return (
                    <TabPanel value={deliveryUnitPricingCatalog.currency}>
                      <JwlTable
                        title="Pieces Pricing Catalog"
                        description="If you have been reading the overrides documentation page but you are, add more metadata..."
                        columns={[
                          'Size Type',
                          'Material Type',
                          'Plating Type',
                          'Stones Placement Type',
                          'Producer Price',
                          'Platform Price',
                          'Merchant Retail Reference Price'
                        ]}
                        rows={getPiecePricingRows()}
                        height="400px"
                        size="small"
                        children={
                          <div style={{ display: 'flex' }}>
                            {!currentplatformSaleCatalog.isPublished && (
                              <UploadButton
                                id={`piecesPricingCatalog_${currentplatformSaleCatalog.id}`}
                                importButtonLabel={'Import'}
                                onFileSelected={(piecePricingCsv: File) => {
                                  importPlatformSaleCatalogPiecePricingCsvIntoCatalog({
                                    variables: {
                                      platformSaleCatalogId: currentplatformSaleCatalog.id,
                                      csv: piecePricingCsv
                                    }
                                  });
                                }}
                              />
                            )}
                            <Button
                              variant="contained"
                              color={'primary'}
                              onClick={() => {
                                exportPlatformSaleCatalogPiecePricingCsvFromCatalog({
                                  variables: {
                                    currency: deliveryUnitPricingCatalog.currency,
                                    platformSaleCatalogId: currentplatformSaleCatalog.id
                                  }
                                });
                              }}
                              style={{ marginLeft: '4px' }}
                            >
                              <LogOut />
                              <span style={{ marginLeft: '4px' }}>Export</span>
                            </Button>
                          </div>
                        }
                      />
                      <StyledDivider />
                      <JwlTable
                        title="Accessories Pricing Catalog"
                        description="If you have been reading the overrides documentation page but you are, add more metadata..."
                        columns={[
                          'Size Type',
                          'Material Type',
                          'Plating Type',
                          'Producer Price',
                          'Platform Price',
                          'Merchant Retail Reference Price'
                        ]}
                        rows={getAccessoryPricingRows()}
                        height="400px"
                        size="small"
                        children={
                          <div style={{ display: 'flex' }}>
                            {!currentplatformSaleCatalog.isPublished && (
                              <UploadButton
                                id={`accessoriesPricingCatalog_${currentplatformSaleCatalog.id}`}
                                importButtonLabel={'Import'}
                                onFileSelected={(accessoryPricingCsv: File) => {
                                  importPlatformSaleCatalogAccessoryPricingCsvIntoCatalog({
                                    variables: {
                                      platformSaleCatalogId: currentplatformSaleCatalog.id,
                                      csv: accessoryPricingCsv
                                    }
                                  });
                                }}
                              />
                            )}
                            <Button
                              variant="contained"
                              color={'primary'}
                              onClick={() => {
                                exportPlatformSaleCatalogAccessoryPricingCsvFromCatalog({
                                  variables: {
                                    currency: deliveryUnitPricingCatalog.currency,
                                    platformSaleCatalogId: currentplatformSaleCatalog.id
                                  }
                                });
                              }}
                              style={{ marginLeft: '4px' }}
                            >
                              <LogOut />
                              <span style={{ marginLeft: '4px' }}>Export</span>
                            </Button>
                          </div>
                        }
                      />
                    </TabPanel>
                  );
                }
              )}
            </TabContext>
          </SectionCard>
        </Grid>
      )}
      <Grid item style={{ marginTop: '8px' }}>
        <SectionCard title="Asset inventory">
          <JwlTable
            title="Pieces Inventory"
            description="If you have been reading the overrides documentation page but you are, add more metadata..."
            columns={['Piece Name', 'Size Type', 'Material Type', 'Plating Type', 'Inventory Reference']}
            rows={getPieceInventoryRows()}
            height="400px"
            size="small"
          />
          <StyledDivider />
          <JwlTable
            title="Accessories Inventory"
            description="If you have been reading the overrides documentation page but you are, add more metadata..."
            columns={['Accessory Name', 'Size Type', 'Material Type', 'Plating Type', 'Inventory Reference']}
            rows={getAccessoriesInventoryRows()}
            height="400px"
            size="small"
          />
        </SectionCard>
      </Grid>
      {showEditDialog && (
        <PlatformSaleCatalogEditDialog
          platformSaleCatalog={currentplatformSaleCatalog}
          onClose={success => {
            setShowEditDialog(false);
            if (success) {
              successNotification(toast, 'Platform sale catalog updated successfully', () => {});
              reloadData();
            }
          }}
        ></PlatformSaleCatalogEditDialog>
      )}
      {showDuplicateDialog && (
        <PlatformSaleCatalogDuplicateDialog
          sourcePlatformSaleCatalog={currentplatformSaleCatalog}
          onClose={(success, _newDeliveryUnitSaleCatalogId) => {
            setShowDuplicateDialog(false);
            if (success) {
              successNotification(toast, 'New platform sale catalog created successfully', () => {});
              history.push(PlatformNavigation.platformSaleCatalogs.pathFromParams({}));
            }
          }}
        ></PlatformSaleCatalogDuplicateDialog>
      )}
    </Fragment>
  );
};

export default PlatformSaleCatalogDetails;
